import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, effectiveTier } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import CreditCostStrip from "@/components/credits/CreditCostStrip";
import SmartExportModal from "@/components/exports/SmartExportModal";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Loader2, Download, Sparkles, Share2, Wand2, RotateCcw, ChevronDown, ChevronUp, Upload, X, Users } from "lucide-react";
import { LEAD_CHARACTERS, findCharacter, characterThumb } from "@/data/leadCharacters";

/* ── Selector option catalogs — tuned for ad / promo / brand use cases ── */
const SUBJECT_TYPES = [
  { id: "product",       label: "Product / SKU" },
  { id: "brand",         label: "Brand / Logo spot" },
  { id: "lifestyle",     label: "Lifestyle moment" },
  { id: "music_promo",   label: "Music / Artist promo" },
  { id: "real_estate",   label: "Real-estate / Property" },
  { id: "food",          label: "Food / Beverage" },
  { id: "fashion",       label: "Fashion / Apparel" },
  { id: "service",       label: "Service / SaaS" },
  { id: "story_scene",   label: "Story / Cinematic scene" },
];

const USE_CASES = [
  { id: "commercial",    label: "Commercial" },
  { id: "social_ad",     label: "Social ad" },
  { id: "promo",         label: "Brand promo" },
  { id: "music_drop",    label: "Music drop" },
  { id: "product_launch",label: "Product launch" },
  { id: "ugc",           label: "UGC-style" },
  { id: "teaser",        label: "Teaser" },
];

const PLATFORMS = [
  { id: "instagram_reels", label: "IG Reels", aspect: "9:16" },
  { id: "tiktok",          label: "TikTok",    aspect: "9:16" },
  { id: "youtube_shorts",  label: "YT Shorts", aspect: "9:16" },
  { id: "ig_feed",         label: "IG Feed",   aspect: "1:1"  },
  { id: "youtube",         label: "YouTube",   aspect: "16:9" },
  { id: "open_web",        label: "Web / Open",aspect: "16:9" },
];

const TONES = [
  { id: "cinematic",   label: "Cinematic" },
  { id: "luxury",      label: "Luxury" },
  { id: "bold",        label: "Bold / High-energy" },
  { id: "clean",       label: "Clean / Minimal" },
  { id: "premium",     label: "Premium" },
  { id: "warm",        label: "Warm / Inviting" },
  { id: "playful",     label: "Playful" },
  { id: "documentary", label: "Documentary" },
];

const VISUAL_STYLES = [
  { id: "modern",       label: "Modern" },
  { id: "vintage_film", label: "Vintage film" },
  { id: "neon_night",   label: "Neon / night" },
  { id: "golden_hour",  label: "Golden hour" },
  { id: "studio",       label: "Studio softbox" },
  { id: "editorial",    label: "Editorial / magazine" },
  { id: "anamorphic",   label: "Anamorphic cinematic" },
];

const ASPECTS = [
  { id: "9:16", label: "Vertical · 9:16" },
  { id: "1:1",  label: "Square · 1:1" },
  { id: "16:9", label: "Widescreen · 16:9" },
];

const DURATIONS = [
  { id: 4,  label: "Quick · 4s" },
  { id: 8,  label: "Standard · 8s" },
  { id: 12, label: "Extended · 12s" },
];

// Stable copy maps used to build the LLM prompt server-side.
const subjectCopy = Object.fromEntries(SUBJECT_TYPES.map(o => [o.id, o.label]));
const useCaseCopy = Object.fromEntries(USE_CASES.map(o => [o.id, o.label]));
const toneCopy    = Object.fromEntries(TONES.map(o => [o.id, o.label]));
const styleCopy   = Object.fromEntries(VISUAL_STYLES.map(o => [o.id, o.label]));

/**
 * buildVideoPrompt
 * --------------------------------------------------------------
 * Turn the structured brief into a tactile Sora-ready prompt.
 * Sora produces much better output when prompts are concrete and
 * comma-separated rather than freeform.
 */
function buildVideoPrompt(brief) {
  const subject = (brief.subject || "").trim();
  if (!subject) return "";

  const parts = [];

  // Lead with what + use case
  const subjLabel = subjectCopy[brief.subjectType] || "subject";
  const ucLabel   = useCaseCopy[brief.useCase] || "commercial";
  parts.push(`${brief.duration}-second ${ucLabel.toLowerCase()} for ${subject}`);
  if (subjLabel && brief.subjectType !== "product" && brief.subjectType !== "brand") {
    parts.push(`category: ${subjLabel.toLowerCase()}`);
  }

  // Lead character — woven in early so Sora anchors composition around them
  const character = findCharacter(brief.characterId);
  if (character) {
    parts.push(`featuring ${character.description}, hero lead in frame`);
  }

  // User-uploaded reference photos — Sora is text-to-video, so we describe
  // the references inline rather than passing pixels. Filenames + count
  // tell Sora to anchor the look to user-supplied imagery.
  if (brief.userAssets?.length) {
    parts.push(`composition referenced from ${brief.userAssets.length} customer-supplied photo${brief.userAssets.length > 1 ? "s" : ""}, match palette / wardrobe / product geometry exactly`);
  }

  // Tone + visual style
  parts.push(`${(toneCopy[brief.tone]   || "cinematic").toLowerCase()} tone`);
  parts.push(`${(styleCopy[brief.style] || "modern").toLowerCase()} visual style`);

  // Optional context blocks
  if (brief.mustInclude?.trim()) parts.push(`must include: ${brief.mustInclude.trim()}`);
  if (brief.cta?.trim())         parts.push(`end with on-screen CTA: "${brief.cta.trim()}"`);

  // Camera + lighting defaults Sora handles well
  parts.push("shallow depth of field, tactile materials, motivated lighting, soft hand-held movement");

  // Negative prompt becomes a trailing "avoid:" clause
  const neg = (brief.negativePrompt || "").trim();
  const base = parts.join(", ");
  return neg ? `${base}. Avoid: ${neg}.` : `${base}.`;
}

export default function VideoGeneratePage() {
  const { user, loading, refresh } = useAuth();

  // Brief state — single object so reuse-brief / autofill is trivial.
  const [brief, setBrief] = useState({
    subject:        "",
    subjectType:    "product",
    useCase:        "commercial",
    platform:       "instagram_reels",
    tone:           "cinematic",
    style:          "modern",
    duration:       4,
    aspect:         "9:16",
    cta:            "",
    mustInclude:    "",
    negativePrompt: "",
    advancedPrompt: "",     // power-user override
    characterId:    "",     // selected YBM character (empty = none)
    userAssets:     [],     // [{asset_id, preview_url, kind, name}]
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");
  const [result,  setResult]  = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // ── Autofill from sessionStorage / query string / reuse-brief
  useEffect(() => {
    // 1. Homepage creator → sessionStorage
    try {
      const raw = sessionStorage.getItem("ybm_autofill_prompt");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.kind === "video") {
          setBrief((b) => ({
            ...b,
            subject: p.subject || p.prompt?.slice(0, 80) || "",
            aspect:  p.aspect || b.aspect,
            advancedPrompt: p.prompt || "",
          }));
          if (p.prompt) setAdvancedOpen(true);
          sessionStorage.removeItem("ybm_autofill_prompt");
        }
      }
    } catch {}
    // 2. Reuse-brief from /renders or another generator
    try {
      const stored = sessionStorage.getItem("ybm_reuse_video_brief");
      if (stored) {
        setBrief((b) => ({ ...b, ...JSON.parse(stored) }));
        sessionStorage.removeItem("ybm_reuse_video_brief");
      }
    } catch {}
    // 3. Query string from Ad Generator (?prompt=&duration=)
    try {
      const q = new URLSearchParams(window.location.search);
      const pq = q.get("prompt");
      const dq = parseInt(q.get("duration") || "", 10);
      if (pq) {
        setBrief((b) => ({ ...b, subject: pq.slice(0, 80), advancedPrompt: pq }));
        setAdvancedOpen(true);
      }
      if (Number.isFinite(dq) && dq >= 4 && dq <= 60) {
        setBrief((b) => ({ ...b, duration: Math.min(12, Math.max(4, dq)) }));
      }
    } catch {}
  }, []);

  // Platform selection auto-sets the recommended aspect (but user can override after)
  const onPlatformChange = (id) => {
    const p = PLATFORMS.find((x) => x.id === id);
    setBrief((b) => ({ ...b, platform: id, aspect: p?.aspect || b.aspect }));
  };

  const assembledPrompt = useMemo(() => {
    const adv = brief.advancedPrompt?.trim();
    return adv ? adv : buildVideoPrompt(brief);
  }, [brief]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  const tier = effectiveTier(user);

  const onGenerate = async () => {
    setError(""); setResult(null);
    if (!assembledPrompt || assembledPrompt.length < 6) {
      setError("Tell us what you want to make — fill in the subject above.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/generate/video", {
        prompt:   assembledPrompt,
        style:    brief.useCase === "music_drop" ? "music-promo"
                : brief.useCase === "social_ad"  ? "social"
                : brief.useCase === "product_launch" ? "product"
                : "commercial",
        aspect:   brief.aspect,
        duration: brief.duration,
        ...(brief.userAssets?.length ? { asset_id: brief.userAssets[0].asset_id } : {}),
      });
      setResult(data);
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onReuseBrief = () => {
    // Drop everything except the brief into the form again, keep the user on
    // the same page so they can tweak one field and re-run.
    setResult(null);
    setError("");
    setExportOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Upload user-supplied reference photos / clips. Stores asset_id + preview URL
  // so the brief carries them through to the prompt and (future) image-to-video.
  const onUploadFiles = async (fileList) => {
    setUploadError("");
    const files = Array.from(fileList || []).slice(0, 5);
    if (!files.length) return;
    setUploading(true);
    try {
      const next = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/asset", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          asset_id: data.asset_id,
          preview_url: data.preview_url,
          kind: data.kind,
          name: file.name,
        });
      }
      setBrief((b) => ({ ...b, userAssets: [...(b.userAssets || []), ...next].slice(0, 8) }));
    } catch (err) {
      setUploadError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAsset = (assetId) => {
    setBrief((b) => ({ ...b, userAssets: (b.userAssets || []).filter((a) => a.asset_id !== assetId) }));
  };

  const downloadHref = result ? `${BACKEND_URL}${result.download_url}` : null;

  return (
    <DashboardShell>
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10" data-testid="video-generator-page">
        {/* Header */}
        <div className="mb-7">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-3">
            <Sparkles className="w-3.5 h-3.5 gold-twinkle" /> Guided · Video Generator · Business-grade footage
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold gold-shimmer">Generate a video</h1>
          <p className="text-[#A0A0A0] text-sm mt-2 max-w-2xl">
            Answer a few quick questions. We'll assemble a tactile Sora-ready prompt for ads, promos, music drops and brand spots.
            {tier === "premium" && <span className="text-[#D4AF37] ml-2">· You're on Premium: rendering with Sora 2 Pro.</span>}
          </p>
        </div>

        {!busy && !result && (
          <>
            <div className="space-y-5 border border-[#1F1F1F] bg-[#0A0A0A] p-5 sm:p-6">
              <Field label="What are you making?" required>
                <input
                  data-testid="video-subject"
                  value={brief.subject}
                  onChange={(e) => setBrief((b) => ({ ...b, subject: e.target.value }))}
                  placeholder="e.g. matte black titanium dive watch · vegan lipstick · new R&B single 'Velvet'"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Selector label="Subject type" value={brief.subjectType} options={SUBJECT_TYPES} onChange={(v) => setBrief((b) => ({ ...b, subjectType: v }))} testId="video-subject-type" />
                <Selector label="Use case"     value={brief.useCase}     options={USE_CASES}    onChange={(v) => setBrief((b) => ({ ...b, useCase: v }))}    testId="video-use-case" />
                <Selector label="Platform"     value={brief.platform}    options={PLATFORMS}    onChange={onPlatformChange}                                  testId="video-platform" />
                <Selector label="Tone / vibe"  value={brief.tone}        options={TONES}        onChange={(v) => setBrief((b) => ({ ...b, tone: v }))}       testId="video-tone" />
                <Selector label="Visual style" value={brief.style}       options={VISUAL_STYLES} onChange={(v) => setBrief((b) => ({ ...b, style: v }))}     testId="video-style" />
                <Selector label="Aspect"       value={brief.aspect}      options={ASPECTS}      onChange={(v) => setBrief((b) => ({ ...b, aspect: v }))}     testId="video-aspect" />
                <Selector label="Length"       value={brief.duration}    options={DURATIONS}    onChange={(v) => setBrief((b) => ({ ...b, duration: v }))}   testId="video-duration" />
              </div>

              <Field label="On-screen CTA (optional)">
                <input
                  data-testid="video-cta"
                  value={brief.cta}
                  onChange={(e) => setBrief((b) => ({ ...b, cta: e.target.value }))}
                  placeholder="e.g. Shop now · Pre-order · Listen on Spotify"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              <Field label="Must-include details (optional)">
                <textarea
                  data-testid="video-must-include"
                  rows={2}
                  value={brief.mustInclude}
                  onChange={(e) => setBrief((b) => ({ ...b, mustInclude: e.target.value }))}
                  placeholder="e.g. visible brand color is gold · model wears all black · slow camera dolly-in"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0] resize-none"
                />
              </Field>

              <Field label="Avoid (optional)">
                <input
                  data-testid="video-negative"
                  value={brief.negativePrompt}
                  onChange={(e) => setBrief((b) => ({ ...b, negativePrompt: e.target.value }))}
                  placeholder="e.g. no people · no text overlays · no logos"
                  className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
                />
              </Field>

              {/* ── Lead character — pick a YBM cast member to lead the spot ── */}
              <div className="border-t border-[#1F1F1F] pt-5" data-testid="video-character-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#F4F4F0] font-semibold">Lead character (optional)</span>
                  </div>
                  {brief.characterId && (
                    <button
                      type="button"
                      onClick={() => setBrief((b) => ({ ...b, characterId: "" }))}
                      data-testid="video-character-clear"
                      className="text-[10px] uppercase tracking-wider text-[#A0A0A0] hover:text-[#D4AF37]"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#A0A0A0] mb-3">Pick a YBM cast member to lead your commercial — or leave blank for product-only shots.</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
                  {LEAD_CHARACTERS.map((c) => {
                    const active = brief.characterId === c.id;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setBrief((b) => ({ ...b, characterId: active ? "" : c.id }))}
                        data-testid={`video-character-${c.id}`}
                        className={`group relative aspect-[4/5] overflow-hidden border transition-all ${
                          active
                            ? "border-[#D4AF37] shadow-[0_0_24px_-6px_rgba(212,175,55,0.6)]"
                            : "border-[#2A2A2A] hover:border-[#D4AF37]/60"
                        }`}
                      >
                        <img
                          src={characterThumb(c.id)}
                          alt={c.name}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-1.5">
                          <p className={`text-[10px] font-bold leading-tight ${active ? "text-[#D4AF37]" : "text-white"}`}>{c.name}</p>
                          <p className="text-[8px] uppercase tracking-wider text-[#A0A0A0] truncate">{c.vibe}</p>
                        </div>
                        {active && (
                          <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 bg-[#D4AF37] text-[#080808] text-[10px] font-bold rounded-full">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Bring your own pictures — upload reference imagery ── */}
              <div className="border-t border-[#1F1F1F] pt-5" data-testid="video-uploads-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Upload className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#F4F4F0] font-semibold">Add your own pictures (optional)</span>
                  </div>
                  <span className="text-[10px] text-[#5A5A5A]">Up to 8 · max 25 MB ea</span>
                </div>
                <p className="text-xs text-[#A0A0A0] mb-3">Upload product shots, brand stills or your own talent photos. We'll anchor the look — palette, wardrobe, geometry — to your references.</p>
                <label
                  htmlFor="video-upload-input"
                  data-testid="video-upload-trigger"
                  className={`flex flex-col items-center justify-center gap-2 border border-dashed py-6 px-4 cursor-pointer transition-colors ${
                    uploading
                      ? "border-[#D4AF37]/40 bg-[#D4AF37]/5"
                      : "border-[#2A2A2A] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5"
                  }`}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 text-[#D4AF37]" />
                  )}
                  <span className="text-xs text-[#F4F4F0]">{uploading ? "Uploading…" : "Tap to upload images"}</span>
                  <span className="text-[10px] text-[#5A5A5A]">PNG · JPG · WEBP</span>
                  <input
                    id="video-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploading}
                    onChange={(e) => onUploadFiles(e.target.files)}
                    className="hidden"
                  />
                </label>
                {uploadError && (
                  <p className="mt-2 text-xs text-red-400" data-testid="video-upload-error">{uploadError}</p>
                )}
                {brief.userAssets?.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="video-upload-thumbs">
                    {brief.userAssets.map((a) => (
                      <div key={a.asset_id} className="relative aspect-square border border-[#2A2A2A] overflow-hidden group">
                        <img src={`${BACKEND_URL}${a.preview_url}`} alt={a.name} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeAsset(a.asset_id)}
                          data-testid={`video-upload-remove-${a.asset_id}`}
                          className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 bg-black/80 text-white hover:bg-red-500 transition-colors"
                          aria-label="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Advanced freeform — power-user override */}
              <div className="border-t border-[#1F1F1F] pt-4">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  data-testid="video-advanced-toggle"
                  className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0] hover:text-[#D4AF37] flex items-center gap-1.5"
                >
                  {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Advanced · Use my own prompt
                </button>
                {advancedOpen && (
                  <textarea
                    data-testid="video-advanced-prompt"
                    rows={4}
                    value={brief.advancedPrompt}
                    onChange={(e) => setBrief((b) => ({ ...b, advancedPrompt: e.target.value }))}
                    placeholder="Override the guided brief with a full custom prompt. Leave empty to use the structured fields above."
                    className="mt-3 w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#F4F4F0] resize-none font-mono"
                  />
                )}
              </div>
            </div>

            {/* Live prompt preview */}
            {assembledPrompt && (
              <div className="mt-4 px-4 py-3 border border-[#1F1F1F] bg-[#080808]" data-testid="video-prompt-preview">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] mb-1">Prompt we'll send</p>
                <p className="text-xs text-[#A0A0A0] leading-relaxed">{assembledPrompt}</p>
              </div>
            )}

            <CreditCostStrip feature="video" quality="pro" duration={brief.duration} label={`${brief.duration}s · Pro`} />

            {error && <div className="mb-4 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm" data-testid="video-gen-error">{error}</div>}

            <button
              onClick={onGenerate}
              disabled={busy}
              data-testid="video-generate-button"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 gold-sheen hover:brightness-110 text-[#080808] font-bold py-3.5 text-sm uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_32px_-8px_rgba(212,175,55,0.55)]"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering… (2–5 min)</>
                : <><Wand2 className="w-4 h-4" /> Generate Video</>}
            </button>
            <p className="text-[10px] text-[#5A5A5A] uppercase tracking-wider mt-3 text-center">
              Every render debits credits from your wallet · Failed renders auto-refund
            </p>
          </>
        )}

        {busy && (
          <div className="border border-[#1F1F1F] bg-[#0A0A0A] min-h-[420px] flex items-center justify-center p-6 mt-4" data-testid="video-loading-state">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
              <p className="text-sm text-[#F4F4F0] font-semibold">Rendering your video…</p>
              <p className="text-xs text-[#A0A0A0] mt-2 max-w-xs mx-auto">
                Sora 2 typically takes 2–5 minutes for short clips. Please keep this tab open.
              </p>
            </div>
          </div>
        )}

        {!busy && result && (
          <div className="space-y-5" data-testid="video-result">
            <video
              src={`${BACKEND_URL}${result.preview_url}`}
              controls
              playsInline
              className="w-full h-auto max-h-[70vh] bg-black"
            />
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
              <span className="text-xs text-[#A0A0A0]">
                {result.watermarked ? "Watermarked (free trial)" : "No watermark"} · {result.tier_at_render} · {result.model}
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={onReuseBrief}
                  data-testid="video-reuse-brief"
                  className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reuse brief
                </button>
                <button
                  onClick={() => setExportOpen(true)}
                  data-testid="video-smart-export"
                  className="inline-flex items-center gap-2 border border-[#D4AF37]/50 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2.5 text-sm transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" /> Smart Export
                </button>
                <a
                  href={downloadHref}
                  data-testid="video-download-button"
                  className="inline-flex items-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] px-4 py-2.5 text-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> Download MP4
                </a>
              </div>
            </div>
            {user?.role === "owner" && result.key_source && (
              <div
                data-testid="video-routing-pill"
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider border ${
                  result.fallback_used
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                    : result.key_source === "user"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : result.key_source === "platform"
                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-blue-500/40 bg-blue-500/10 text-blue-300"
                }`}
                title={result.attempts?.length ? `Tiers tried: ${result.attempts.map(a => `${a.key_source}${a.success ? "✓" : "✗"}`).join(" → ")}` : ""}
              >
                <span className="font-bold">Routing:</span>
                <span>{result.fallback_used ? `Fallback used → billed to ${labelForSource(result.key_source)}` : `Billed to ${labelForSource(result.key_source)}`}</span>
              </div>
            )}
          </div>
        )}

        <SmartExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          renderId={result?.render_id}
          sourceAspect={brief.aspect}
          sourceKind="video"
        />
      </div>
    </DashboardShell>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </span>
      {children}
    </label>
  );
}

function Selector({ label, value, options, onChange, testId }) {
  return (
    <Field label={label}>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(typeof options[0].id === "number" ? Number(e.target.value) : e.target.value)}
        className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </Field>
  );
}

function labelForSource(src) {
  if (src === "user")     return "your API key";
  if (src === "platform") return "YBM platform key";
  if (src === "emergent") return "Universal Key (fallback)";
  return src;
}
